import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Tree,
  NodeModel,
  MultiBackend,
  getBackendOptions,
} from "@minoru/react-dnd-treeview";
import { DndProvider } from "react-dnd";
import {
  getAllFolders,
  getSubFolders,
} from "../../services/folderServices";
import FolderIcon from "@mui/icons-material/Folder";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import IconButton from "@mui/material/IconButton";
import { locale } from "../../utils/LocaleManager";
import { nodeStyle } from "./styled";
import { useFolderStore } from "../../stores/folderStore";
import { useContentStore } from "../../stores/contentStore";
import { darkGray } from "../../utils/StyleHelpers";
import { Tooltip, Alert, Button as MuiButton } from "@mui/material";
import { toast } from "react-toastify";
import { isTextNeedsEllipsis } from "../../utils/utilFunctions";
import { handleError } from "../../utils/utilFunctions";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";

const ROOT_FOLDER = {
  id: 1,
  parent: 0,
  text: locale.all_media,
  droppable: true,
  isOpen: true,
};

export const getLoadedDescendantIds = (
  treeData: NodeModel[],
  sourceFolderId: NodeModel["id"],
) => {
  const descendantIds = new Set<NodeModel["id"]>();
  const pendingParentIds = [sourceFolderId];

  while (pendingParentIds.length > 0) {
    const parentId = pendingParentIds.shift();
    treeData.forEach((node) => {
      if (node.parent === parentId && !descendantIds.has(node.id)) {
        descendantIds.add(node.id);
        pendingParentIds.push(node.id);
      }
    });
  }

  return Array.from(descendantIds);
};

export const TreeView = ({
  isClickedBreadCrumb,
  setIsClickedBreadCrumb,
  isClickedFolder,
  setIsClickedFolder,
  onClickNewFolder,
  moveFolderSourceId,
  onMoveDestinationUnavailableIds,
  onMoveDestinationSelected,
  autoFocus = false,
}: any) => {
  const [treeData, setTreeData] = useState<NodeModel[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<number>(ROOT_FOLDER.id);
  const [moveDestinationNodeId, setMoveDestinationNodeId] = useState<NodeModel["id"] | null>(null);
  const [treeError, setTreeError] = useState<string>("");
  const [focusedNodeId, setFocusedNodeId] = useState<NodeModel["id"]>(ROOT_FOLDER.id);
  const [loadingNodeIds, setLoadingNodeIds] = useState<Set<NodeModel["id"]>>(new Set());
  const [liveAnnouncement, setLiveAnnouncement] = useState<string>("");
  const lastFailedOperationRef = useRef<(() => Promise<void>) | null>(null);
  // A monotonic generation invalidates stale in-flight fetch announcements.
  const liveGenRef = useRef<number>(0);
  // Keep the shared loading indicator visible until all concurrent expansions finish.
  const pendingFetchCountRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingFocusNodeIdRef = useRef<NodeModel["id"] | null>(null);
  const autofocusDoneRef = useRef(false);

  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );

  const moveDestinationUnavailableIds = useMemo(() => {
    const ids =
      moveFolderSourceId === undefined || moveFolderSourceId === null
        ? []
        : [
            moveFolderSourceId,
            ...getLoadedDescendantIds(treeData, moveFolderSourceId),
          ];

    if (onMoveDestinationSelected && !rolePermissions?.folder?.root) {
      ids.push(ROOT_FOLDER.id);
    }

    return ids;
  }, [moveFolderSourceId, treeData, rolePermissions, onMoveDestinationSelected]);
  const visibleTreeData = useMemo(() => {
    const isRootHidden = moveDestinationUnavailableIds.includes(ROOT_FOLDER.id);

    return treeData
      .filter((node) => !moveDestinationUnavailableIds.includes(node.id))
      .map((node) =>
        isRootHidden && node.parent === ROOT_FOLDER.id
          ? { ...node, parent: 0 }
          : node,
      );
  }, [moveDestinationUnavailableIds, treeData]);
  const { setIsLoading, setSelectedThumbnails, isShowMoveDialog } =
    useContentStore((state: any) => ({
      setIsLoading: state.setIsLoading,
      setSelectedThumbnails: state.setSelectedThumbnails,
      isShowMoveDialog: state.isShowMoveDialog,
    }));
  const treeRef = useRef<any>(null);

  const {
    openedNodes,
    setFolderData,
    setFolderId,
    setBreadcrumbPath,
    setFolderName,
    setIsShowFolders,
    isRenameSuccess,
    isCreateSuccess,
    deleteRevision,
    setPreviousFolderName,
    isMoveSuccess,
    setPreviousFolderId,
    isShowMoveFolder,
    isFolderMoveSuccess,
    setOpenFolderName,
  } = useFolderStore((state: any) => ({
    openedNodes: state.openedNodes,
    setFolderData: state.setFolderData,
    setFolderId: state.setFolderId,
    setBreadcrumbPath: state.setBreadcrumbPath,
    setFolderName: state.setFolderName,
    setIsShowFolders: state.setIsShowFolders,
    isRenameSuccess: state.isRenameSuccess,
    isCreateSuccess: state.isCreateSuccess,
    deleteRevision: state.deleteRevision,
    setPreviousFolderName: state.setPreviousFolderName,
    isMoveSuccess: state.isMoveSuccess,
    setPreviousFolderId: state.setPreviousFolderId,
    isShowMoveFolder: state.isShowMoveFolder,
    isFolderMoveSuccess: state.isFolderMoveSuccess,
    setOpenFolderName: state.setOpenFolderName,
  }));

  const updateTreeData = (newData: any[], parentId: number) => {
    if (!Array.isArray(newData)) return [];
    return newData.map((node: any) => ({
      ...node,
      parent: parentId,
      isOpen: node.id === ROOT_FOLDER.id ? ROOT_FOLDER.isOpen : node.isOpen,
    }));
  };

  const normalizeStatus = (response: any): number | undefined => {
    return response?.status ?? response?.response?.status;
  };

  const fetchROOT_FOLDERs = async () => {
    try {
      setIsLoading(true);
      const response = await getAllFolders(isShowMoveDialog || isShowMoveFolder);
      const status = normalizeStatus(response);

      if (status === 200) {
        const ROOT_FOLDERs = updateTreeData(response.data, ROOT_FOLDER.id);
        setTreeData([ROOT_FOLDER, ...ROOT_FOLDERs]);
        if (!isShowMoveDialog && !isShowMoveFolder) {
          setFolderData([...ROOT_FOLDERs]);
        }
        setTreeError("");
        lastFailedOperationRef.current = null;
      } else if (status === 401 || status === 403) {
        handleError({ status, toast });
        setTreeError("");
        lastFailedOperationRef.current = null;
      } else {
        setTreeError("Failed to load folders. Please try again.");
        lastFailedOperationRef.current = () => fetchROOT_FOLDERs();
      }
    } catch (error) {
      console.error("Error fetching root folders:", error);
      setTreeError("Failed to load folders. Please try again.");
      lastFailedOperationRef.current = () => fetchROOT_FOLDERs();
    } finally {
      setIsLoading(false);
    }
  };

  const mergeSubFoldersIntoTree = (subFolders: any[]) => {
    const newSubFolders = subFolders.filter(
      (subFolder: any) =>
        !treeData.some((treeItem: any) => treeItem.id === subFolder.id),
    );
    setTreeData((prevTreeData) =>
      [...prevTreeData, ...newSubFolders].filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((treeItem: any) => treeItem.id === item.id),
      ),
    );
  };

  const fetchSubFolders = async (item: any) => {
    if (item?.id === ROOT_FOLDER.id) return;

    const gen = ++liveGenRef.current;
    try {
      pendingFetchCountRef.current++;
      setIsLoading(true);
      setLoadingNodeIds((prev) => new Set(prev).add(item.id));
      setLiveAnnouncement(`${item.text} loading`);
      const response = await getSubFolders(item?.id);
      const status = normalizeStatus(response);

      const isStale = liveGenRef.current !== gen;

      if (status === 200) {
        // Always merge children for a still-expanded branch, even when stale.
        const subFolders = updateTreeData(response.data, item.id);
        mergeSubFoldersIntoTree(subFolders);
        if (!isStale) {
          setTreeError("");
          setLiveAnnouncement(`${item.text} loaded`);
          lastFailedOperationRef.current = null;
        }
      } else if (status === 401 || status === 403) {
        // Auth failures always surface via toast, even from stale requests.
        handleError({ status, toast });
        if (!isStale) {
          setTreeError("");
          setLiveAnnouncement('');
          lastFailedOperationRef.current = null;
        }
      } else {
        if (!isStale) {
          setTreeError("Failed to load subfolders. Please try again.");
          lastFailedOperationRef.current = () => fetchSubFolders(item);
          setLiveAnnouncement('');
        }
      }
    } catch (error) {
      console.error("Error fetching subfolders:", error);
      if (liveGenRef.current === gen) {
        setTreeError("Failed to load subfolders. Please try again.");
        lastFailedOperationRef.current = () => fetchSubFolders(item);
        setLiveAnnouncement('');
      }
    } finally {
      pendingFetchCountRef.current--;
      if (pendingFetchCountRef.current === 0) {
        setIsLoading(false);
      }
      setLoadingNodeIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const getUpdateFolderData = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await getSubFolders(id);
      const status = normalizeStatus(response);

      if (status === 200) {
        setFolderId(id);
        const subFolders = updateTreeData(response.data, id);
        setTreeData((prevTreeData) => {
          const descendantIds = new Set(getLoadedDescendantIds(prevTreeData, id));
          return [
            ...prevTreeData.filter((treeItem) => !descendantIds.has(treeItem.id)),
            ...subFolders,
          ];
        });

        if (subFolders.length !== 0) {
          setIsShowFolders(true);
          setFolderData([...subFolders]);
        } else {
          setFolderData([]);
          setIsShowFolders(false);
        }
        setTreeError("");
        lastFailedOperationRef.current = null;
      } else if (status === 401 || status === 403) {
        handleError({ status, toast });
        setTreeError("");
        lastFailedOperationRef.current = null;
      } else {
        setTreeError("Failed to load folder data. Please try again.");
        lastFailedOperationRef.current = () => getUpdateFolderData(id);
      }
    } catch (error) {
      console.error("Error fetching subfolders:", error);
      setTreeError("Failed to load folder data. Please try again.");
      lastFailedOperationRef.current = () => getUpdateFolderData(id);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBreadcrumbPath = (node: NodeModel) => {
    if (!isShowMoveDialog && !isShowMoveFolder) {
      const path: NodeModel[] = [];
      let currentNode: NodeModel | undefined = node;
      while (currentNode) {
        path.unshift(currentNode);
        // eslint-disable-next-line no-loop-func
        currentNode = treeData.find((item) => item.id === currentNode?.parent);
      }
      setBreadcrumbPath(path);
    }
  };

  const handleNodeClick = async (node: any) => {
    setIsLoading(true);
    // Defer selection until authorization succeeds so a 401/403 cannot
    // leave a denied folder as the current selection (AC8).
    const priorSelectedNodeId = selectedNodeId;

    if (!isShowMoveDialog && !isShowMoveFolder) {
      if (node?.id === ROOT_FOLDER?.id) {
        try {
          const response = await getAllFolders(false);
          const status = normalizeStatus(response);
          if (status === 200) {
            setSelectedNodeId(node?.id);
            setFolderId(node?.id);
            setFolderName(node?.text);
            setOpenFolderName(node?.text);
            setPreviousFolderName(node?.text);
            setPreviousFolderId(node.id);
            updateBreadcrumbPath(ROOT_FOLDER);
            setIsShowFolders(true);
            setSelectedThumbnails([]);
            const ROOT_FOLDERs = updateTreeData(response.data, ROOT_FOLDER.id);
            setFolderData(ROOT_FOLDERs);
            setTreeError("");
            lastFailedOperationRef.current = null;
          } else if (status === 401 || status === 403) {
            handleError({ status, toast });
            setSelectedNodeId(priorSelectedNodeId);
            setTreeError("");
            lastFailedOperationRef.current = null;
          } else {
            setSelectedNodeId(priorSelectedNodeId);
            setTreeError("Failed to load folders. Please try again.");
            lastFailedOperationRef.current = () => handleNodeClick(node);
          }
        } catch (error) {
          console.error("Error fetching root folders:", error);
          setSelectedNodeId(priorSelectedNodeId);
          setTreeError("Failed to load folders. Please try again.");
          lastFailedOperationRef.current = () => handleNodeClick(node);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      try {
        const response = await getSubFolders(node.id);
        const status = normalizeStatus(response);
        if (status === 200) {
          setSelectedNodeId(node?.id);
          setFolderId(node?.id);
          setFolderName(node?.text);
          setOpenFolderName(node?.text);
          setPreviousFolderName(node?.text);
          setPreviousFolderId(node.id);
          const subFolders = updateTreeData(response.data, node.id);
          if (subFolders.length !== 0) {
            setFolderData([...subFolders]);
            mergeSubFoldersIntoTree(subFolders);
            setIsShowFolders(true);
          } else {
            setFolderData([]);
            setIsShowFolders(false);
          }
          setTreeError("");
          lastFailedOperationRef.current = null;
          treeRef.current?.open(node.id);
          updateBreadcrumbPath(node);
        } else if (status === 401 || status === 403) {
          handleError({ status, toast });
          setSelectedNodeId(priorSelectedNodeId);
          setTreeError("");
          lastFailedOperationRef.current = null;
        } else {
          setSelectedNodeId(priorSelectedNodeId);
          setTreeError("Failed to load subfolders. Please try again.");
          lastFailedOperationRef.current = () => handleNodeClick(node);
        }
      } catch (error) {
        console.error("Error fetching subfolders:", error);
        setSelectedNodeId(priorSelectedNodeId);
        setTreeError("Failed to load subfolders. Please try again.");
        lastFailedOperationRef.current = () => handleNodeClick(node);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Move-dialog mode: select destination without browse side effects
      onMoveDestinationSelected?.(node);
      setMoveDestinationNodeId(node.id);
    }
  };

  useEffect(() => {
    onMoveDestinationUnavailableIds?.(moveDestinationUnavailableIds);
  }, [moveDestinationUnavailableIds, onMoveDestinationUnavailableIds]);

  useEffect(() => {
    setFolderId(ROOT_FOLDER.id);
    fetchROOT_FOLDERs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateBreadcrumbPath(ROOT_FOLDER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isClickedBreadCrumb) {
      handleNodeClick(isClickedBreadCrumb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickedBreadCrumb]);

  useEffect(() => {
    if (isClickedFolder) {
      (async () => {
        try {
          await handleNodeClick(isClickedFolder);
        } finally {
          // Consume the folder-card request after every terminal result
          // so the same rendered card can be double-clicked again
          setIsClickedFolder?.(undefined);
        }
      })();
      // State mutations (setFolderName, setFolderId, etc.) are deferred to
      // handleNodeClick's success paths so 401/403 cannot leave denied
      // transients as current (AC8).
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickedFolder]);

  useEffect(() => {
    if (
      isRenameSuccess ||
      isCreateSuccess ||
      deleteRevision > 0 ||
      isMoveSuccess ||
      isFolderMoveSuccess
    ) {
      if (selectedNodeId === ROOT_FOLDER.id) {
        fetchROOT_FOLDERs();
      } else {
        getUpdateFolderData(selectedNodeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRenameSuccess,
    isCreateSuccess,
    deleteRevision,
    isMoveSuccess,
    isFolderMoveSuccess,
  ]);

  useEffect(() => {
    if (autoFocus && !autofocusDoneRef.current && treeData.length > 0 && containerRef.current) {
      const firstVisibleNode = visibleTreeData[0];
      const firstTreeItem = containerRef.current.querySelector('[role="treeitem"]') as HTMLElement;
      if (firstVisibleNode && firstTreeItem) {
        setFocusedNodeId(firstVisibleNode.id);
        firstTreeItem.focus();
        autofocusDoneRef.current = true;
      }
    }
  }, [autoFocus, treeData, visibleTreeData]);

  // Focus first child after async load completes (for ArrowRight on closed branch)
  useEffect(() => {
    if (pendingFocusNodeIdRef.current && loadingNodeIds.size === 0) {
      const parentNodeId = pendingFocusNodeIdRef.current;
      const firstChild = treeData.find((n) => n.parent === parentNodeId);
      if (firstChild) {
        const childId = String(firstChild.id);
        setFocusedNodeId(childId as any);
        const treeElement = containerRef.current?.querySelector('[role="tree"]');
        if (treeElement) {
          const childElement = treeElement.querySelector(
            `[data-testid="tree-node-${CSS.escape(childId)}"]`
          ) as HTMLElement;
          if (childElement) {
            childElement.focus();
          }
        }
      }
      pendingFocusNodeIdRef.current = null;
    }
  }, [loadingNodeIds, treeData]);

  const handleTreeRetry = async () => {
    setTreeError("");
    if (lastFailedOperationRef.current) {
      await lastFailedOperationRef.current();
    }
  };

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <div className="custom-treeview" ref={containerRef}>
        {treeError && (
          <Alert
            severity="error"
            role="alert"
            sx={{ mb: 2 }}
            action={
              <MuiButton color="inherit" size="small" onClick={handleTreeRetry}>
                Retry
              </MuiButton>
            }
          >
            {treeError}
          </Alert>
        )}
        <div aria-live="polite" role="status" style={{ position: 'absolute', left: '-9999px' }}>
          {liveAnnouncement}
        </div>
        <Tree
          ref={treeRef}
          initialOpen={openedNodes}
          sort={true}
          tree={visibleTreeData}
          rootId={0}
          canDrag={() => false}
          onDrop={() => {}}
          render={(node, { depth, isOpen, onToggle }) => (
            <Tooltip
              title={isTextNeedsEllipsis(node.text) ? node.text : ""}
              placement="top-end"
              arrow
            >
              <div
                className="folder-item"
                style={{
                  marginLeft: depth * 20,
                  ...nodeStyle,
                  backgroundColor:
                    (isShowMoveDialog || isShowMoveFolder
                      ? moveDestinationNodeId
                      : selectedNodeId) === node.id
                      ? "#C1E7FE"
                      : "",
                }}
                onClick={() => {
                  setFocusedNodeId(node.id);
                  if (isShowMoveDialog || isShowMoveFolder) {
                    onMoveDestinationSelected?.(node);
                    setMoveDestinationNodeId(node.id);
                  } else {
                    handleNodeClick(node);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setFocusedNodeId(node.id);
                    if (isShowMoveDialog || isShowMoveFolder) {
                      onMoveDestinationSelected?.(node);
                      setMoveDestinationNodeId(node.id);
                    } else {
                      handleNodeClick(node);
                    }
                  } else if (e.key === "ArrowRight") {
                    e.preventDefault();
                    if (node.droppable && !isOpen) {
                      // Expand closed branch and focus first child after load
                      pendingFocusNodeIdRef.current = node.id;
                      onToggle();
                      liveGenRef.current++;
                      setLiveAnnouncement(`${node.text} expanded`);
                      setTimeout(() => fetchSubFolders(node), 0);
                    } else if (node.droppable && isOpen) {
                      // Move to first child if expanded
                      const firstChild = treeData.find((n) => n.parent === node.id);
                      if (firstChild) {
                        const childId = String(firstChild.id);
                        setFocusedNodeId(childId as any);
                        const treeElement = containerRef.current?.querySelector('[role="tree"]');
                        if (treeElement) {
                          const childElement = treeElement.querySelector(
                            `[data-testid="tree-node-${CSS.escape(childId)}"]`
                          ) as HTMLElement;
                          if (childElement) {
                            childElement.focus();
                          }
                        }
                      }
                    }
                  } else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    if (isOpen) {
                      // Collapse open branch
                      onToggle();
                      liveGenRef.current++;
                      setLiveAnnouncement(`${node.text} collapsed`);
                    } else {
                      // Move to parent if closed
                      const parentNode = treeData.find((n) => n.id === node.parent);
                      if (parentNode) {
                        const parentId = String(parentNode.id);
                        setFocusedNodeId(parentId as any);
                        const treeElement = containerRef.current?.querySelector('[role="tree"]');
                        if (treeElement) {
                          const parentElement = treeElement.querySelector(
                            `[data-testid="tree-node-${CSS.escape(parentId)}"]`
                          ) as HTMLElement;
                          if (parentElement) {
                            parentElement.focus();
                          }
                        }
                      }
                    }
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    const treeElement = containerRef.current?.querySelector('[role="tree"]');
                    if (!treeElement) return;
                    const nodes = treeElement.querySelectorAll('[role="treeitem"]');
                    const currentIndex = Array.from(nodes).findIndex(n => n.getAttribute('data-testid') === `tree-node-${node.id}`);
                    if (currentIndex >= 0 && currentIndex < nodes.length - 1) {
                      const nextNode = nodes[currentIndex + 1] as HTMLElement;
                      const nextId = nextNode.getAttribute('data-testid')?.replace('tree-node-', '');
                      if (nextId) setFocusedNodeId(nextId as any);
                      nextNode.focus();
                    }
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    const treeElement = containerRef.current?.querySelector('[role="tree"]');
                    if (!treeElement) return;
                    const nodes = treeElement.querySelectorAll('[role="treeitem"]');
                    const currentIndex = Array.from(nodes).findIndex(n => n.getAttribute('data-testid') === `tree-node-${node.id}`);
                    if (currentIndex > 0) {
                      const prevNode = nodes[currentIndex - 1] as HTMLElement;
                      const prevId = prevNode.getAttribute('data-testid')?.replace('tree-node-', '');
                      if (prevId) setFocusedNodeId(prevId as any);
                      prevNode.focus();
                    }
                  } else if (e.key === "Home") {
                    e.preventDefault();
                    const treeElement = containerRef.current?.querySelector('[role="tree"]');
                    if (!treeElement) return;
                    const firstNode = treeElement.querySelector('[role="treeitem"]') as HTMLElement;
                    if (firstNode) {
                      const firstId = firstNode.getAttribute('data-testid')?.replace('tree-node-', '');
                      if (firstId) setFocusedNodeId(firstId as any);
                      firstNode.focus();
                    }
                  } else if (e.key === "End") {
                    e.preventDefault();
                    const treeElement = containerRef.current?.querySelector('[role="tree"]');
                    if (!treeElement) return;
                    const nodes = treeElement.querySelectorAll('[role="treeitem"]');
                    if (nodes.length > 0) {
                      const lastNode = nodes[nodes.length - 1] as HTMLElement;
                      const lastId = lastNode.getAttribute('data-testid')?.replace('tree-node-', '');
                      if (lastId) setFocusedNodeId(lastId as any);
                      lastNode.focus();
                    }
                  }
                }}
                role="treeitem"
                tabIndex={focusedNodeId === node.id ? 0 : -1}
                aria-label={`${node.text} folder`}
                aria-expanded={node.droppable ? isOpen : undefined}
                aria-selected={
                  (isShowMoveDialog || isShowMoveFolder
                    ? moveDestinationNodeId
                    : selectedNodeId) === node.id
                }
                aria-busy={loadingNodeIds.has(node.id) ? true : undefined}
                data-testid={`tree-node-${node.id}`}
              >
                {node.droppable && (
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <IconButton
                      data-testid={`chevron-${node.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                        liveGenRef.current++;
                        if (isOpen) {
                          setLiveAnnouncement(`${node.text} collapsed`);
                        } else {
                          setLiveAnnouncement(`${node.text} expanded`);
                          setTimeout(() => fetchSubFolders(node), 0);
                        }
                      }}
                      tabIndex={-1}
                      aria-label={isOpen ? `Collapse ${node.text}` : `Expand ${node.text}`}
                      aria-expanded={isOpen}
                      size="small"
                      sx={{ padding: 0 }}
                    >
                      {isOpen ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                    </IconButton>
                    {node.parent === 0 ? (
                      <VideoLibraryIcon
                        style={{ color: darkGray }}
                        sx={{ width: "1.25rem", height: "1.25rem" }}
                      />
                    ) : (
                      <FolderIcon
                        style={{ color: darkGray }}
                        sx={{ width: "1.25rem", height: "1.25rem" }}
                      />
                    )}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {node.text}
                    </span>
                  </span>
                )}
              </div>
            </Tooltip>
          )}
        />
      </div>
    </DndProvider>
  );
};

export default TreeView;
